import type { EntityViewType, StaticEntityType } from "@config";
import { useIsEditorContext } from "@editor-providers";
import { hasBackground, hasBorder } from "@library/shared";
import classNames from "classnames";
import type { CSSProperties, ReactNode } from "react";
import "../styles/index.scss";
import type {
  EditorDataProps,
  EditorSlotsProps,
  SetSettingsType,
} from "../types";
import { SetStyled } from "./Set.styled";

export const SetView = ({
  entity,
  children,
  editorData,
  editorSlots,
}: {
  children: ReactNode;
  entity: EntityViewType;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const isEditor = useIsEditorContext();
  const { settings, id } = entity as StaticEntityType<
    SetSettingsType & { index: number; comboName: string } & {
      combo?: Record<string, unknown>;
    }
  >;
  return (
    <SetStyled
      className={classNames("set set-wrapper-XFGzfwO910", {
        "--active": editorData?.isActive,
        "--active-thread": editorData?.isActiveThread,
      })}
      {...DATA_ATTRIBUTES({ settings })}
      {...STYLED_ATTRIBUTES({ settings })}
      {...INLINE_STYLE_ATTRIBUTES({ settings })}
      {...EDITOR_ATTRIBUTES({ id, isEditor, active: editorData?.isActive })}
    >
      {editorSlots?.actionsSlot}
      {editorSlots?.sideActionsSlot}
      <div className="setInnerWrapper">{children}</div>
      {editorSlots?.entityThreadLabel}
      {editorSlots?.coeditingEntityOutline}
    </SetStyled>
  );
};

//  ======= ATTRIBUTE CREATION =======
const STYLED_ATTRIBUTES = ({ settings }: { settings: SetSettingsType }) => ({
  $groupsRatio: settings.groupsRatio ?? ["50", "50"],
  $horizontalAlignment: settings.horizontalAlignment,
  $verticalAlignment: settings.verticalAlignment,
  $borderRadius: settings.round,
  $borderWidth: settings.borderWidth,
  $borderColor: settings.borderColor,
  $backgroundColor: settings.backgroundColor,
});

const DATA_ATTRIBUTES = ({
  settings,
}: {
  settings: SetSettingsType & {
    index: number;
    comboName: string;
    combo?: Record<string, unknown>;
  };
}) => {
  const payload: Record<string, string | number | boolean> = {
    "data-anchor-target": settings.isAnchorActive ? settings.anchorName : "",
    "data-set-index": settings.index,
    "data-has-background": hasBackground(settings.backgroundColor),
    "data-has-border": hasBorder(settings),
  };
  if (settings.comboName === "FullScreen") {
    payload["data-fullscreen-media-position"] =
      settings.mediaPosition || settings.combo?.mediaPosition;
    payload["data-fullscreen-layout-style"] =
      settings.layoutStyle || settings.combo?.layoutStyle;
    payload["data-fullscreen-groups-ratio"] =
      settings.groupsRatio.join("-") ||
      (settings.combo?.groupsRatio as number[])?.join("-");
  }
  return payload;
};

const EDITOR_ATTRIBUTES = ({
  id,
  isEditor = false,
  active,
}: {
  id: string;
  isEditor: boolean;
  active?: boolean;
}) => {
  const payload: Record<string, string | number> = {
    "data-layer-id": id,
    "data-layer-type": "set",
  };
  if (active && isEditor) payload["tabIndex"] = 0;
  return payload;
};

const INLINE_STYLE_ATTRIBUTES = ({
  settings,
}: {
  settings: SetSettingsType & {
    comboName: string;
    combo?: Record<string, unknown>;
  };
}) => {
  if (settings.comboName !== "FullScreen") return {};
  const isDiagonal =
    settings.layoutStyle === "diagonal" ||
    settings.combo?.layoutStyle === "diagonal";

  let polygon = "polygon(25% 0, 100% 0%, 100% 100%, 0% 100%)";

  if (!settings.mediaPosition) {
    if (settings.combo?.mediaPosition === "left")
      polygon = "polygon(0 0, 100% 0%, 75% 100%, 0% 100%)";
  } else if (settings.mediaPosition === "left") {
    polygon = "polygon(0 0, 100% 0%, 75% 100%, 0% 100%)";
  }

  const mobilePolygonTop = "polygon(0 25%, 100% 0%, 100% 100%, 0 100%)";
  const style = {} as Record<string, string>;
  if (isDiagonal) {
    style["--media-layout-style"] = polygon;
    style["--mobile-media-layout-top"] = mobilePolygonTop;
  }

  return {
    style: style as CSSProperties,
  };
};
//  ======= ATTRIBUTE CREATION =======
